import { css } from 'styled-components';
import { math } from 'polished';
import { SDS_MOTION_DURATION_DEFAULT, SDS_MOTION_EASING_LINEAR, SDS_MOTION_EASING_EXIT } from '@s-group/design-system-tokens/web/tokens/motion.es6.js';
import { entry, loopStretch, loopRotate } from './bar.keyframes.js';
import { entryRotate, loopRotate as loopRotate$1, loopStrokeHead, entryTail, loopStrokeTail } from './circle.keyframes.js';
import { ANGLE_0, ANGLE_90, ANGLE_270 } from '../IndicatorBase/constants.js';
import { PrimaryPath, Indicator, SecondaryPath } from '../IndicatorBase/IndicatorBase.styles.js';
import { strokeDasharray } from '../IndicatorBase/util.js';
import { SDS } from '../common/namespace.js';

const circleEntryAnimationDuration = math(`2 * ${SDS_MOTION_DURATION_DEFAULT}`);
const barEntryAnimationDuration = SDS_MOTION_DURATION_DEFAULT;
const initialDelay = SDS_MOTION_DURATION_DEFAULT;
const loopStrokeAnimationDuration = math(`4 * ${SDS_MOTION_DURATION_DEFAULT}`);
const loopRotateAnimationDuration = math(`4 * ${loopStrokeAnimationDuration}`);
const loopRotateReducedAnimationDuration = math(`2 * ${loopStrokeAnimationDuration}`);
const reducedMotion = (styles) => css `
    /* Reduced motion forced in component props */
    ${({ reducedMotion }) => reducedMotion && styles}
    @media screen and (prefers-reduced-motion) {
        /* Reduced motion set in system preferences */
        ${styles}
    }
`;
const LoaderStyle = css `
    --initial-delay: ${({ delay }) => (delay ? initialDelay : '0ms')};
    --entry-animation-duration: ${({ type }) => type === 'bar'
    ? barEntryAnimationDuration
    : circleEntryAnimationDuration};
    --loop-animation-delay: calc(
        var(--initial-delay) + var(--entry-animation-duration)
    );
    --loop-rotate-animation-duration: ${loopRotateAnimationDuration};
    --loop-primary-stroke-dasharray: ${strokeDasharray(ANGLE_0)};
    --disable-on-reduced-motion: initial;
    --enable-on-reduced-motion: none;

    color: ${({ theme }) => { var _a; return (_a = theme[SDS].colors) === null || _a === void 0 ? void 0 : _a.SDS_BRAND_COLOR_ELEMENT_STRONG_PRIMARY; }};

    ${({ type }) => reducedMotion(css `
            --entry-animation-duration: 0ms;
            --loop-rotate-animation-duration: ${loopRotateReducedAnimationDuration};
            --loop-primary-stroke-dasharray: ${type === 'bar'
    ? strokeDasharray(ANGLE_90)
    : strokeDasharray(ANGLE_270)};
            --disable-on-reduced-motion: none;
            --enable-on-reduced-motion: initial;
        `)}

    /* Stylelint is unable to resolve styled component names used in styles below */
    /* stylelint-disable no-duplicate-selectors */

    &[data-type='bar'] {
        ${PrimaryPath} {
            animation: var(
                    --disable-on-reduced-motion,
                    ${entry} var(--entry-animation-duration)
                        cubic-bezier(${SDS_MOTION_EASING_LINEAR})
                        var(--initial-delay) 1 forwards
                ),
                var(
                    --disable-on-reduced-motion,
                    ${loopStretch} ${loopStrokeAnimationDuration}
                        var(--loop-animation-delay) infinite
                ),
                ${loopRotate} var(--loop-rotate-animation-duration)
                    cubic-bezier(${SDS_MOTION_EASING_LINEAR})
                    var(--loop-animation-delay) infinite;
            stroke-dasharray: var(--loop-primary-stroke-dasharray);
        }
    }

    &[data-type='circle'] {
        ${Indicator} {
            animation: var(
                    --disable-on-reduced-motion,
                    ${entryRotate} var(--entry-animation-duration)
                        cubic-bezier(${SDS_MOTION_EASING_LINEAR})
                        var(--initial-delay) 1
                ),
                ${loopRotate$1} var(--loop-rotate-animation-duration)
                    cubic-bezier(${SDS_MOTION_EASING_LINEAR})
                    var(--loop-animation-delay) infinite;
            opacity: 0;
        }

        ${PrimaryPath} {
            animation: var(
                --disable-on-reduced-motion,
                ${loopStrokeHead} ${loopStrokeAnimationDuration}
                    var(--loop-animation-delay) infinite
            );
            stroke-dasharray: var(--loop-primary-stroke-dasharray);
        }

        ${SecondaryPath} {
            animation: var(
                --disable-on-reduced-motion,
                ${entryTail} var(--entry-animation-duration)
                    cubic-bezier(${SDS_MOTION_EASING_EXIT}) var(--initial-delay)
                    1,
                ${loopStrokeTail} ${loopStrokeAnimationDuration}
                    cubic-bezier(${SDS_MOTION_EASING_EXIT})
                    var(--loop-animation-delay) infinite
            );
        }
    }
`;

export { LoaderStyle };
//# sourceMappingURL=Loader.styles.js.map
